"use client";

import type React from "react";
import { useRef } from "react";
import { Plus, X, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { TagInput } from "@/components/tag-input";
import type {
  CustomQuestion,
  QuestionType,
  SettingsData,
} from "@/services/types";

// Helper function to convert label to snake_case field name
const generateFieldName = (label: string): string => {
  return label
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "") // Remove special characters except spaces and hyphens
    .replace(/\s+/g, "_") // Replace spaces with underscores
    .replace(/-+/g, "_") // Replace hyphens with underscores
    .replace(/_+/g, "_") // Replace multiple underscores with single
    .replace(/^_+|_+$/g, ""); // Remove leading/trailing underscores
};

// Default question names that should not be editable or removable
const DEFAULT_QUESTION_NAMES = ["name", "email", "phone_number"];

const FILE_FORMAT_OPTIONS = [
  { label: "PDF", value: "pdf" },
  { label: "JPG", value: "jpg" },
  { label: "JPEG", value: "jpeg" },
  { label: "PNG", value: "png" },
  { label: "GIF", value: "gif" },
  { label: "DOC", value: "doc" },
  { label: "DOCX", value: "docx" },
  { label: "XLS", value: "xls" },
  { label: "XLSX", value: "xlsx" },
  { label: "PPT", value: "ppt" },
  { label: "PPTX", value: "pptx" },
  { label: "CSV", value: "csv" },
];

interface QuestionRowProps {
  question: CustomQuestion;
  onChange: (q: CustomQuestion) => void;
  onRemove: () => void;
  canRemove?: boolean;
  isDefault?: boolean;
}

const QuestionRow: React.FC<QuestionRowProps> = ({
  question,
  onChange,
  onRemove,
  canRemove = true,
  isDefault = false,
}) => {
  const isSelect =
    question.type === "singleselect" || question.type === "multiselect";
  const isFile = question.type === "file";
  const isNumber = question.type === "number";
  const isText = question.type === "text";
  const isTextarea = question.type === "textarea";

  // Check if select type has no options
  const hasNoOptions =
    isSelect && (!question.options || question.options.length === 0);

  const update = (patch: Partial<CustomQuestion>) =>
    onChange({ ...question, ...patch });

  // Convert options array to string array of labels for TagInput
  const optionLabels = (question.options || []).map((opt) => opt.label);

  const handleOptionsChange = (labels: string[]) => {
    // Convert string labels back to {value, label} format
    const newOptions = labels.map((label) => ({
      value: label.toLowerCase().replace(/\s+/g, "-"),
      label: label,
    }));
    update({ options: newOptions });
  };

  return (
    <div className="rounded-lg border border-input p-4 space-y-4 bg-muted/30">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-foreground">
          Question #{question.order}
        </div>
        <Button
          type="button"
          variant="ghost"
          className="text-destructive"
          onClick={onRemove}
          disabled={!canRemove || isDefault}
          title={
            isDefault
              ? "This is a default question and cannot be removed"
              : !canRemove
                ? "At least one question is required"
                : "Remove question"
          }
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Label</label>
          <Input
            value={question.label}
            onChange={(e) => {
              const newLabel = e.target.value;
              // Always auto-generate name from label
              const generatedName = newLabel ? generateFieldName(newLabel) : "";
              update({ label: newLabel, name: generatedName });
            }}
            placeholder="Enter label"
            className={`bg-muted/50 border-input ${
              isDefault
                ? "disabled:opacity-100 disabled:text-foreground disabled:font-medium"
                : ""
            }`}
            disabled={isDefault}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Type</label>
          <Select
            value={question.type}
            onValueChange={(val) => {
              const newType = val as QuestionType;
              const isSelectType =
                newType === "singleselect" || newType === "multiselect";
              const updates: Partial<CustomQuestion> = {
                type: newType,
                // Only keep options if it's a select type, otherwise remove it
                options: isSelectType ? question.options || [] : undefined,
                fileConfig: undefined,
                numberConfig: undefined,
                textConfig: undefined,
              };
              // Set default configs when switching to types that require them
              if (newType === "text") {
                updates.textConfig = { maxLength: 50 };
              } else if (newType === "textarea") {
                updates.textConfig = { maxLength: 1000 };
              } else if (newType === "file") {
                updates.fileConfig = { maxFiles: 1, allowedFormats: [] };
              } else if (newType === "number") {
                updates.numberConfig = { min: 0, max: 0 };
              }
              update(updates);
            }}
            disabled={isDefault}
          >
            <SelectTrigger
              className={`bg-muted/50 border-input h-12 ${
                isDefault
                  ? "disabled:opacity-100 disabled:text-foreground disabled:font-medium"
                  : ""
              }`}
            >
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="text">Text</SelectItem>
              <SelectItem value="singleselect">Single Select</SelectItem>
              <SelectItem value="multiselect">Multi Select</SelectItem>
              <SelectItem value="url">URL</SelectItem>
              <SelectItem value="file">File</SelectItem>
              <SelectItem value="number">Number</SelectItem>
              <SelectItem value="textarea">Text Area</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Required</label>
          <Select
            value={question.required ? "yes" : "no"}
            onValueChange={(v) => update({ required: v === "yes" })}
            disabled={isDefault}
          >
            <SelectTrigger
              className={`bg-muted/50 border-input h-12 ${
                isDefault
                  ? "disabled:opacity-100 disabled:text-foreground disabled:font-medium"
                  : ""
              }`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isText && (
          <div className="w-full gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Max length
              </label>
              <Input
                type="number"
                value={question.textConfig?.maxLength ?? 50}
                onChange={(e) =>
                  update({
                    textConfig: { maxLength: Number(e.target.value) || 50 },
                  })
                }
                className={`bg-muted/50 border-input ${
                  isDefault
                    ? "disabled:opacity-100 disabled:text-foreground disabled:font-medium"
                    : ""
                }`}
                disabled={isDefault}
              />
            </div>
          </div>
        )}

        {isTextarea && (
          <div className="w-full gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Max length
              </label>
              <Input
                type="number"
                value={question.textConfig?.maxLength ?? 1000}
                onChange={(e) =>
                  update({
                    textConfig: { maxLength: Number(e.target.value) || 1000 },
                  })
                }
                className={`bg-muted/50 border-input ${
                  isDefault
                    ? "disabled:opacity-100 disabled:text-foreground disabled:font-medium"
                    : ""
                }`}
                disabled={isDefault}
              />
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            Placeholder (optional)
          </label>
          <Input
            value={question.placeholder || ""}
            onChange={(e) => update({ placeholder: e.target.value })}
            placeholder={
              question.label
                ? `e.g. Enter ${question.label.toLowerCase()}`
                : "Enter placeholder"
            }
            className={`bg-muted/50 border-input ${
              isDefault
                ? "disabled:opacity-100 disabled:text-foreground disabled:font-medium"
                : ""
            }`}
            disabled={isDefault}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">
            Help text (optional)
          </label>
          <Input
            value={question.helpText || ""}
            onChange={(e) => update({ helpText: e.target.value })}
            placeholder="Short hint for applicants"
            className={`bg-muted/50 border-input ${
              isDefault
                ? "disabled:opacity-100 disabled:text-foreground disabled:font-medium"
                : ""
            }`}
            disabled={isDefault}
          />
        </div>
      </div>

      {isSelect && (
        <div className="space-y-3">
          <label className="block text-sm font-medium">Options</label>
          <TagInput
            tags={optionLabels}
            setTags={handleOptionsChange}
            placeholder="Add option and press Enter or space bar"
            className="bg-gray-50 h-12"
            disabled={isDefault}
          />
          {hasNoOptions && (
            <p className="text-destructive text-sm">
              At least one option is required for select type questions.
            </p>
          )}
        </div>
      )}

      {isFile && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Max files</label>
            <Input
              type="number"
              min={1}
              value={question.fileConfig?.maxFiles ?? 1}
              onChange={(e) =>
                update({
                  fileConfig: {
                    maxFiles: Number(e.target.value) || 1,
                    allowedFormats: question.fileConfig?.allowedFormats || [],
                  },
                })
              }
              className={`bg-muted/50 border-input ${
                isDefault
                  ? "disabled:opacity-100 disabled:text-foreground disabled:font-medium"
                  : ""
              }`}
              disabled={isDefault}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              Allowed formats
            </label>
            <div className="grid grid-cols-2 gap-2 border border-input rounded-lg p-3 bg-muted/30">
              {FILE_FORMAT_OPTIONS.map((format) => {
                const selectedFormats = question.fileConfig?.allowedFormats || [];
                const isSelected = selectedFormats.includes(format.value);
                return (
                  <label
                    key={format.value}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => {
                        const nextFormats = isSelected
                          ? selectedFormats.filter(
                              (value) => value !== format.value
                            )
                          : [...selectedFormats, format.value];
                        update({
                          fileConfig: {
                            maxFiles: question.fileConfig?.maxFiles || 1,
                            allowedFormats: nextFormats,
                          },
                        });
                      }}
                      disabled={isDefault}
                    />
                    <span className="text-sm text-foreground">
                      {format.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {isNumber && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Min</label>
            <Input
              type="number"
              value={question.numberConfig?.min ?? 0}
              onChange={(e) =>
                update({
                  numberConfig: {
                    min: Number(e.target.value) || 0,
                    max: question.numberConfig?.max ?? 0,
                  },
                })
              }
              className={`bg-muted/50 border-input ${
                isDefault
                  ? "disabled:opacity-100 disabled:text-foreground disabled:font-medium"
                  : ""
              }`}
              disabled={isDefault}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Max</label>
            <Input
              type="number"
              value={question.numberConfig?.max ?? 0}
              onChange={(e) =>
                update({
                  numberConfig: {
                    min: question.numberConfig?.min ?? 0,
                    max: Number(e.target.value) || 0,
                  },
                })
              }
              className={`bg-muted/50 border-input ${
                isDefault
                  ? "disabled:opacity-100 disabled:text-foreground disabled:font-medium"
                  : ""
              }`}
              disabled={isDefault}
            />
          </div>
        </div>
      )}
    </div>
  );
};

interface StepTwoFormProps {
  customQuestions: CustomQuestion[];
  setCustomQuestions: React.Dispatch<React.SetStateAction<CustomQuestion[]>>;
  settings: SettingsData;
  setSettings: React.Dispatch<React.SetStateAction<SettingsData>>;
  status: string;
  setStatus: React.Dispatch<React.SetStateAction<string>>;
  isExternal?: boolean;
}

export function StepTwoForm({
  customQuestions,
  setCustomQuestions,
  settings,
  setSettings,
  status,
  setStatus,
  isExternal = false,
}: StepTwoFormProps) {
  const dateInputRef = useRef<HTMLInputElement>(null);
  const addNewQuestion = () => {
    const nextOrder = customQuestions.length + 1;
    setCustomQuestions([
      ...customQuestions,
      {
        name: "",
        label: "",
        type: "multiselect",
        required: true,
        options: [],
        order: nextOrder,
      },
    ]);
  };

  return (
    <>
      {/* Custom Questions Builder */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <label className="block text-base font-semibold text-foreground">
            Custom Questions
          </label>
          {customQuestions.length === 0 && (
            <Button type="button" onClick={addNewQuestion} className="gap-2">
              <Plus className="w-4 h-4" /> Add Question
            </Button>
          )}
        </div>
        {customQuestions.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No questions added yet. Click "Add Question" to begin.
          </p>
        )}
        <div className="space-y-4">
          {customQuestions.map((q, idx) => {
            const isDefault = DEFAULT_QUESTION_NAMES.includes(q.name);
            return (
              <div key={idx} className="space-y-4">
                <QuestionRow
                  question={q}
                  onChange={(updated) =>
                    setCustomQuestions((prev) =>
                      prev.map((pq, i) => (i === idx ? updated : pq))
                    )
                  }
                  onRemove={() => {
                    // Prevent deletion if this is the last question or if it's a default question
                    if (customQuestions.length === 1 || isDefault) {
                      return;
                    }
                    setCustomQuestions((prev) =>
                      prev.filter((_, i) => i !== idx)
                    );
                  }}
                  canRemove={customQuestions.length > 1}
                  isDefault={isDefault}
                />
                {idx === customQuestions.length - 1 && (
                  <Button
                    type="button"
                    onClick={addNewQuestion}
                    variant="outline"
                    className="gap-2 w-full"
                  >
                    <Plus className="w-4 h-4" /> Add Question
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Settings */}
      <div className="space-y-4">
        <label className="block text-base font-semibold text-foreground">
          Settings
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {!isExternal && (
            <div>
              <label className="block text-sm font-medium mb-2">
                Submission deadline
              </label>
              <div className="relative">
                <Input
                  ref={dateInputRef}
                  type="datetime-local"
                  value={
                    settings.submissionDeadline
                      ? (() => {
                          const date = new Date(settings.submissionDeadline);
                          // Convert to local datetime format (YYYY-MM-DDTHH:mm)
                          const year = date.getFullYear();
                          const month = String(date.getMonth() + 1).padStart(
                            2,
                            "0"
                          );
                          const day = String(date.getDate()).padStart(2, "0");
                          const hours = String(date.getHours()).padStart(2, "0");
                          const minutes = String(date.getMinutes()).padStart(
                            2,
                            "0"
                          );
                          return `${year}-${month}-${day}T${hours}:${minutes}`;
                        })()
                      : ""
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      // Convert datetime-local format to ISO string
                      // datetime-local gives us local time, we need to create a Date object
                      const localDate = new Date(value);
                      setSettings((s) => ({
                        ...s,
                        submissionDeadline: localDate.toISOString(),
                      }));
                    } else {
                      setSettings((s) => ({
                        ...s,
                        submissionDeadline: "",
                      }));
                    }
                  }}
                  onClick={(e) => {
                    // Prevent native calendar from opening on input click
                    e.preventDefault();
                  }}
                  className="bg-gray-50 h-12 pr-10 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden [&:invalid:not(:focus)]:text-transparent"
                  style={{
                    color: settings.submissionDeadline
                      ? "inherit"
                      : "transparent",
                  }}
                />
                {!settings.submissionDeadline && (
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none select-none">
                    Enter date
                  </span>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    if (
                      dateInputRef.current &&
                      dateInputRef.current.showPicker
                    ) {
                      dateInputRef.current.showPicker();
                    } else if (dateInputRef.current) {
                      dateInputRef.current.focus();
                      dateInputRef.current.click();
                    }
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none cursor-pointer z-10"
                  aria-label="Open calendar"
                >
                  <Calendar className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
          {!isExternal && (
            <div>
              <label className="block text-sm font-medium mb-2">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="bg-muted/50 border-input h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Confirmation email
          </label>
          <Textarea
            value={settings.confirmationEmail}
            onChange={(e) =>
              setSettings((s) => ({
                ...s,
                confirmationEmail: e.target.value,
              }))
            }
            rows={4}
            className="bg-muted/50 border-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Notification Emails
          </label>
          <p className="text-xs text-muted-foreground mb-3">
            Add email addresses that should receive notifications when someone
            applies to this opportunity. You can add multiple emails by pressing
            Enter or Space after each email address.
          </p>
          <TagInput
            tags={settings.notificationEmails ?? []}
            setTags={(emails) =>
              setSettings((s) => ({ ...s, notificationEmails: emails }))
            }
            placeholder="Add email and press Enter or space bar"
            className="bg-gray-50 h-12"
          />
        </div>
      </div>
    </>
  );
}
